import { NgModule, Type } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { InputViewComponent } from './input-view/input-view.component';
import { RegistrationCardComponent } from '../onboarding/registration-card/registration-card.component';
import { VariantDHeroComponent } from './variant-d-hero/variant-d-hero.component';
import { ButtonComponent } from './button/button.component';
import { SelectInputComponent } from './select-input/select-input.component';
import { TextComponent } from './text/text.component';
import { DateAgoPipe } from './pipes/date-ago.pipe';
import { NgxSmartModalModule } from 'ngx-smart-modal';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgbDatepickerModule, NgbDropdownModule, NgbHighlight, NgbProgressbar, NgbProgressbarModule, NgbTooltipModule, NgbTypeaheadModule } from '@ng-bootstrap/ng-bootstrap';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { LoaderComponent } from './loader/loader.component';
import { SwitchComponent } from './switch/switch.component';
import { NumberInputComponent } from './number-input/number-input.component';
import { PasswordFieldComponent } from './password-field/password-field.component';
import { IntegrationAnimationComponent } from './integration-animation/integration-animation.component';
import { ValidationErrorComponent } from './validation-error/validation-error.component';
import { TextInputAddonComponent } from './text-input-addon/text-input-addon.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { AlertComponent } from './alert/alert.component';
import { FileUploadComponent } from './file-upload/file-upload.component';
import { DropdownComponent } from './dropdown/dropdown.component';
import { TruncateTextPipe } from './pipes/truncate-text.pipe';
import { LinkifyPipe } from './pipes/linkify.pipe';
import { DisplayFieldComponent } from './display-field/display-field.component';
import { DataHookDirective } from './directives/data-hook.directive';
import { NavigatorComponent } from './navigator/navigator.component';
import { UpdatedAtPipe } from './pipes/updated-at.pipe';
import { ShimmerDirective } from './directives/shimmer.directive';
import { ClickOutsideDirective } from './directives/click-outside.directive';
import { ScrollIntoViewOnInitDirective } from './directives/scroll-into-view-on-init.directive';
import { FieldValueCopyComponent } from './field-value-copy/field-value-copy.component';
import { InfoTileComponent } from './info-tile/info-tile.component';
import { HoverComponent } from './hover/hover.component';
import { RouterModule } from '@angular/router';
import { SvgIconComponent } from './svg/svg.component';
import { RadioGroupComponent } from './radio-group/radio-group.component';
import { ImageUploadComponent } from './image-upload/image-upload.component';
import { RosieCurrencyPipe } from './pipes/rosie-currency.pipe';
import { SubscriptionComponent } from './subscription/subscription.component';
import { DateFormatPipe } from './date-format.pipe';
import { PlyrModule } from '@atom-platform/ngx-plyr';
import { TypeaheadComponent } from './typeahead/typeahead.component';
import { PhoneMaskDirective } from './phone-mask.directive';
import { NgxMaskModule } from 'ngx-mask';
import { PhoneInputComponent } from './phone-input/phone-input.component';
import { PhoneNumberPipe } from './pipes/phone-number.pipe';
import { NgCircleProgressModule } from 'ng-circle-progress';
import { PossessivePipe } from './possessive/possessive.pipe';
import { CapitalizePipe } from './capitalize.pipe';
import { NgIconsModule } from '@ng-icons/core';
import { heroArrowRight, heroArrowsRightLeft, heroCalendarDateRange, heroChatBubbleLeftRight, heroCheck, heroCheckCircle, heroChevronDown, heroChevronUp, heroCreditCard, heroCursorArrowRays, heroExclamationTriangle, heroGift, heroLink, heroMagnifyingGlass, heroPencilSquare, heroPhoneArrowUpRight, heroQuestionMarkCircle, heroSparkles, heroSquare2Stack, heroXCircle, heroXMark, heroBuildingStorefront, heroCalendarDays, heroChatBubbleOvalLeftEllipsis, heroDevicePhoneMobile, heroLifebuoy, heroLockClosed, heroPaperAirplane, heroPhone } from '@ng-icons/heroicons/outline';
import { heroChatBubbleBottomCenterTextSolid, heroChevronDownSolid, heroCog6ToothSolid, heroExclamationCircleSolid, heroExclamationTriangleSolid, heroSparklesSolid, heroTagSolid } from '@ng-icons/heroicons/solid';
import { heroArrowRightMini, heroArrowUpRightMini, heroInformationCircleMini, heroPauseMini, heroPhoneArrowUpRightMini, heroPlayMini, heroPlusMini } from '@ng-icons/heroicons/mini';
import { VoicePreviewButtonComponent } from './voice-preview-button/voice-preview-button.component';
import { ChoosePlanSectionComponent } from './choose-plan-section/choose-plan-section.component';
import { SetupChecklistComponent } from './setup-checklist/setup-checklist.component';
import { TestNumbersComponent } from './test-numbers/test-numbers.component';
import { GooglePlacesSearchComponent } from './google-places-search/google-places-search.component';
import { AgentSetupProcessComponent } from './agent-setup-process/agent-setup-process.component';
import { PillsComponent } from './pills/pills.component';
import { ScheduleHoursComponent } from './schedule-hours/schedule-hours.component';
import { CollapsibleSectionComponent } from './collapsible-section/collapsible-section.component';
import { BillingAlertComponent } from '../nav-sidebar/billing-alert/billing-alert.component';
import { RosieNumberComponent } from './rosie-number/rosie-number.component';
import { ProcessIndicatorComponent } from './process-indicator/process-indicator.component';
import { DisableModalRefocusDirective } from './directives/disable-modal-refocus.directive';
import { ModalScrollDirective } from './directives/modal-scroll.directive';
import { CustomGreetingEditorComponent } from '../configuration/greeting/custom-greeting-editor/custom-greeting-editor.component';
import { QuillModule } from 'ngx-quill';
import { LaunchInstructionsOverviewComponent } from './launch-instructions-overview/launch-instructions-overview.component';
import { SubscriptionPlansCheckoutComponent } from './subscription/subscription-plans-checkout/subscription-plans-checkout.component';
import { AllPlansIncludeComponent } from './subscription/all-plans-include/all-plans-include.component';
import { CouponCodeComponent } from './coupon-code/coupon-code.component';
import { TrialPlansSelectionComponent } from './trial-plans-selection/trial-plans-selection.component';
import { WebsiteTextingSubscribeModalComponent } from './website-texting-subscribe-modal/website-texting-subscribe-modal.component';
import { WebsiteTextingCancelModalComponent } from './website-texting-cancel-modal/website-texting-cancel-modal.component';
import { KeepExploringComponent } from './keep-exploring/keep-exploring.component';
import { PauseSubscriptionModalComponent } from './pause-subscription-modal/pause-subscription-modal.component';
import { AppDownloadBannerComponent } from './app-download-banner/app-download-banner.component';
import { AppRedirectComponent } from './app-redirect/app-redirect.component';
import { AppEmailVerifyComponent } from './app-email-verify/app-email-verify.component';
import { WebsiteTextingBannerComponent } from './website-texting-banner/website-texting-banner.component';
import { WebsiteChatBannerComponent } from './website-chat-banner/website-chat-banner.component';
import { LeadSourcesBannerComponent } from './lead-sources-banner/lead-sources-banner.component';
import { CrmIntegrationsBannerComponent } from './crm-integrations-banner/crm-integrations-banner.component';
import { MessageTakingBannerComponent } from './message-taking-banner/message-taking-banner.component';
import { WaterfallTransfersBannerComponent } from './waterfall-transfers-banner/waterfall-transfers-banner.component';
import { CallRateLimitBannerComponent } from './call-rate-limit-banner/call-rate-limit-banner.component';
import { CardStackComponent } from './card-stack/card-stack.component';
import { StackCardDirective } from './card-stack/stack-card.directive';


@NgModule({
  declarations: [
    RegistrationCardComponent,
    VariantDHeroComponent,
    InputViewComponent,
    ButtonComponent,
    SelectInputComponent,
    TextComponent,
    DateAgoPipe,
    LoaderComponent,
    SwitchComponent,
    LoaderComponent,
    NumberInputComponent,
    PasswordFieldComponent,
    IntegrationAnimationComponent,
    ValidationErrorComponent,
    TextInputAddonComponent,
    AlertComponent,
    FileUploadComponent,
    DropdownComponent,
    TruncateTextPipe,
    LinkifyPipe,
    DisplayFieldComponent,
    DataHookDirective,
    NavigatorComponent,
    UpdatedAtPipe,
    ShimmerDirective,
    ClickOutsideDirective,
    ScrollIntoViewOnInitDirective,
    FieldValueCopyComponent,
    InfoTileComponent,
    HoverComponent,
    SvgIconComponent,
    RadioGroupComponent,
    ImageUploadComponent,
    RosieCurrencyPipe,
    SubscriptionComponent,
    DateFormatPipe,
    TypeaheadComponent,
    PhoneMaskDirective,
    PhoneInputComponent,
    PhoneNumberPipe,
    PossessivePipe,
    CapitalizePipe,
    GooglePlacesSearchComponent,
    AgentSetupProcessComponent,
    PillsComponent,
    ScheduleHoursComponent,
    CollapsibleSectionComponent,
    BillingAlertComponent,
    SubscriptionPlansCheckoutComponent,
    AllPlansIncludeComponent,
    RosieNumberComponent,
    ProcessIndicatorComponent,
    DisableModalRefocusDirective,
    ModalScrollDirective,
    CustomGreetingEditorComponent,
    LaunchInstructionsOverviewComponent,
    CouponCodeComponent,
    ValidationErrorComponent,
    TrialPlansSelectionComponent,
    WebsiteTextingSubscribeModalComponent,
    WebsiteTextingCancelModalComponent,
    KeepExploringComponent,
    PauseSubscriptionModalComponent,
    AppDownloadBannerComponent,
    AppRedirectComponent,
    AppEmailVerifyComponent,
    WebsiteTextingBannerComponent,
    WebsiteChatBannerComponent,
    LeadSourcesBannerComponent,
    CrmIntegrationsBannerComponent,
    MessageTakingBannerComponent,
    WaterfallTransfersBannerComponent,
    CallRateLimitBannerComponent,
    CardStackComponent,
    StackCardDirective,
    VoicePreviewButtonComponent,
    ChoosePlanSectionComponent,
    SetupChecklistComponent,
    TestNumbersComponent
  ],
  imports: [
    NgbProgressbarModule,
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    NgxSmartModalModule.forChild(),
    FontAwesomeModule,
    NgbDropdownModule,
    NgbTypeaheadModule,
    NgbHighlight,
    InfiniteScrollModule,
    NgSelectModule,
    NgbDatepickerModule,
    NgbTooltipModule,
    RouterModule,
    PlyrModule,
    NgbProgressbarModule,
    NgbTypeaheadModule,
    NgxMaskModule.forRoot(),
    NgIconsModule.withIcons({
      heroArrowRight, heroSparklesSolid, heroMagnifyingGlass, heroCheck, heroArrowsRightLeft, heroExclamationCircleSolid, heroChevronUp, heroChevronDown, heroSquare2Stack, heroXCircle, heroTagSolid, heroCheckCircle, heroCreditCard, heroCursorArrowRays, heroPhoneArrowUpRight, heroChatBubbleLeftRight, heroSparkles, heroExclamationTriangle, heroCog6ToothSolid, heroQuestionMarkCircle, heroPencilSquare, heroLink, heroCalendarDateRange, heroExclamationTriangleSolid, heroXMark, heroChatBubbleBottomCenterTextSolid, heroChevronDownSolid, heroArrowRightMini, heroGift,
      // ENG-2292 unified onboarding shared pieces
      heroBuildingStorefront, heroCalendarDays, heroChatBubbleOvalLeftEllipsis, heroDevicePhoneMobile, heroLifebuoy, heroLockClosed, heroPaperAirplane, heroPhone,
      heroArrowUpRightMini, heroInformationCircleMini, heroPauseMini, heroPhoneArrowUpRightMini, heroPlayMini, heroPlusMini
    }),
    NgCircleProgressModule.forRoot({
      "backgroundPadding": 0,
      "radius": 8,
      "space": -3,
      "toFixed": 0,
      "maxPercent": 100,
      "outerStrokeWidth": 3,
      "outerStrokeColor": "#9332e0",
      "outerStrokeGradientStopColor": "#53a9ff",
      "innerStrokeColor": "#dab7fb42",
      "innerStrokeWidth": 3,
      "title": "UI",
      "animateTitle": false,
      "animationDuration": 1000,
      "showTitle": false,
      "showSubtitle": false,
      "showUnits": false,
      "showBackground": false,
      "startFromZero": false,
      "showZeroOuterStroke": false,
      "lazy": true
    }),
    QuillModule
  ],
  exports: [
    VariantDHeroComponent,
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    RegistrationCardComponent,
    InputViewComponent,
    ButtonComponent,
    TextComponent,
    SelectInputComponent,
    DateAgoPipe,
    NgxSmartModalModule,
    FontAwesomeModule,
    NgbTypeaheadModule,
    NgbHighlight,
    NgbDropdownModule,
    LoaderComponent,
    SwitchComponent,
    LoaderComponent,
    NumberInputComponent,
    PasswordFieldComponent,
    IntegrationAnimationComponent,
    ValidationErrorComponent,
    TextInputAddonComponent,
    NgSelectModule,
    AlertComponent,
    FileUploadComponent,
    DropdownComponent,
    TruncateTextPipe,
    LinkifyPipe,
    DisplayFieldComponent,
    NgbDatepickerModule,
    DataHookDirective,
    NavigatorComponent,
    UpdatedAtPipe,
    ShimmerDirective,
    ClickOutsideDirective,
    ScrollIntoViewOnInitDirective,
    FieldValueCopyComponent,
    InfoTileComponent,
    HoverComponent,
    NgbTooltipModule,
    SvgIconComponent,
    RadioGroupComponent,
    ImageUploadComponent,
    RosieCurrencyPipe,
    SubscriptionComponent,
    DateFormatPipe,
    PlyrModule,
    TypeaheadComponent,
    PhoneMaskDirective,
    PhoneInputComponent,
    NgCircleProgressModule,
    PossessivePipe,
    CapitalizePipe,
    GooglePlacesSearchComponent,
    AgentSetupProcessComponent,
    PillsComponent,
    ScheduleHoursComponent,
    CollapsibleSectionComponent,
    BillingAlertComponent,
    SubscriptionPlansCheckoutComponent,
    AllPlansIncludeComponent,
    RosieNumberComponent,
    ProcessIndicatorComponent,
    DisableModalRefocusDirective,
    ModalScrollDirective,
    CustomGreetingEditorComponent,
    LaunchInstructionsOverviewComponent,
    QuillModule,
    PhoneNumberPipe,
    CouponCodeComponent,
    TrialPlansSelectionComponent,
    WebsiteTextingSubscribeModalComponent,
    WebsiteTextingCancelModalComponent,
    KeepExploringComponent,
    PauseSubscriptionModalComponent,
    AppDownloadBannerComponent,
    WebsiteTextingBannerComponent,
    WebsiteChatBannerComponent,
    LeadSourcesBannerComponent,
    CrmIntegrationsBannerComponent,
    MessageTakingBannerComponent,
    WaterfallTransfersBannerComponent,
    CallRateLimitBannerComponent,
    CardStackComponent,
    StackCardDirective,
    VoicePreviewButtonComponent,
    ChoosePlanSectionComponent,
    SetupChecklistComponent,
    TestNumbersComponent
  ]
})
export class SharedModule {
}
